import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import {
	defaultLocale,
	normalizeLocale,
	supportedLocales,
	type LocaleCode,
} from "@/config/locales";

import en from "../../public/locales/en.json";

const loadedLocales = new Set<LocaleCode>([defaultLocale]);

export async function loadLanguage(locale: string): Promise<LocaleCode> {
	const normalized = normalizeLocale(locale);

	if (loadedLocales.has(normalized)) {
		return normalized;
	}

	try {
		const response = await fetch(`/locales/${normalized}.json`, {
			cache: "force-cache",
		});

		if (!response.ok) {
			throw new Error(
				`Failed to load locale ${normalized}: ${response.status}`,
			);
		}

		const resources = (await response.json()) as Record<string, unknown>;
		i18next.addResourceBundle(normalized, "translation", resources, true, true);
		loadedLocales.add(normalized);
	} catch (error) {
		console.warn("Could not load locale, using English fallback", {
			locale: normalized,
			error,
		});
		return defaultLocale;
	}

	return normalized;
}

if (!i18next.isInitialized) {
	void i18next
		.use(LanguageDetector)
		.use(initReactI18next)
		.init({
			resources: {
				[defaultLocale]: {
					translation: en,
				},
			},
			fallbackLng: defaultLocale,
			supportedLngs: supportedLocales,
			defaultNS: "translation",
			interpolation: {
				escapeValue: false,
			},
			react: {
				useSuspense: false,
			},
			detection: {
				order: ["querystring", "cookie", "localStorage", "navigator"],
				lookupQuerystring: "locales",
				lookupCookie: "locales",
				lookupLocalStorage: "locales",
				caches: ["cookie", "localStorage"],
			},
		})
		.then(() => {
			if (typeof document !== "undefined") {
				document.documentElement.lang = normalizeLocale(
					i18next.resolvedLanguage,
				);
			}
		});
}

export default i18next;
